import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { HelpModal } from "./HelpModal";
import { APP_VERSION } from "../version";
import {
  Users, Briefcase, User, Shield, LogOut, Menu, X, Plus, ChevronRight, Gift, Calendar, Handshake, UserPlus, BarChart2, LayoutDashboard, Rocket, HelpCircle
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import UpdatesButton from "./UpdatesButton";
import { SponsorModal } from "./SponsorModal";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_opport-biz-ma/artifacts/oknd9jsa_Logo%20Club%20Business%20Maroc%20Annuaire.png";

function NavItem({ to, icon: Icon, label, onClick, testId, rightSlot, end }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      end={end}
      data-testid={testId}
      className={({ isActive }) => `cbm-nav-link ${isActive ? "active" : ""}`}
    >
      <Icon size={17} />
      <span className="flex-1">{label}</span>
      {rightSlot}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [showSponsor, setShowSponsor] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/stats`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Sidebar = ({ onClose }) => (
    <div className="cbm-sidebar flex flex-col h-full w-64" data-testid="sidebar">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: "rgba(155,169,202,0.1)" }}>
        <img src={LOGO_URL} alt="CBM" className="w-10 h-10 object-contain rounded-lg" />
        <div>
          <div className="font-outfit font-bold text-white text-sm leading-tight">CLUB BUSINESS</div>
          <div className="font-outfit font-semibold text-gold text-xs tracking-[0.15em]">MAROC</div>
        </div>
        {onClose && (
          <button className="ml-auto text-cbm-muted hover:text-white" onClick={onClose}>
            <X size={18} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <p className="cbm-label px-2 mb-3">Navigation</p>
        <NavItem to="/demarrage" icon={Rocket} label="Démarrage" onClick={onClose} testId="nav-demarrage" />
        <NavItem to="/dashboard" icon={LayoutDashboard} label="Tableau de bord" onClick={onClose} testId="nav-dashboard" />
        <NavItem to="/annuaire" icon={Users} label="Annuaire" onClick={onClose} testId="nav-annuaire" />
        <NavItem
          to="/opportunites"
          icon={Briefcase}
          label="Annonces"
          onClick={onClose}
          testId="nav-opportunites"
          rightSlot={stats?.open_opportunities > 0 && (
            <span className="flex items-center gap-1" data-testid="nav-opportunites-count">
              {stats.new_opportunities_week > 0 && (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-gold inline-block"
                  title={`${stats.new_opportunities_week} nouvelle${stats.new_opportunities_week > 1 ? "s" : ""} cette semaine`}
                  data-testid="nav-opportunites-new-dot"
                />
              )}
              <span className="text-xs text-cbm-muted font-medium tabular-nums">
                {stats.open_opportunities}
              </span>
            </span>
          )}
        />
        <NavItem
          to="/avantages"
          icon={Gift}
          label="Avantages"
          onClick={onClose}
          end
          testId="nav-avantages"
          rightSlot={stats?.available_benefits > 0 && (
            <span className="flex items-center gap-1" data-testid="nav-avantages-count">
              {stats.new_benefits_week > 0 && (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-gold inline-block"
                  title={`${stats.new_benefits_week} nouveau${stats.new_benefits_week > 1 ? "x" : ""} cette semaine`}
                  data-testid="nav-avantages-new-dot"
                />
              )}
              <span className="text-xs text-cbm-muted font-medium tabular-nums">
                {stats.available_benefits}
              </span>
            </span>
          )}
        />
        <NavItem to="/evenements" icon={Calendar} label="Événements" onClick={onClose} testId="nav-evenements" />

        <div className="pt-4 border-t cbm-divider mt-4">
          <p className="cbm-label px-2 mb-3">Mon espace</p>
          <NavItem to="/mon-profil" icon={User} label="Mon Profil" onClick={onClose} testId="nav-mon-profil" />
          <NavItem to="/prospects" icon={Handshake} label="Prospects" onClick={onClose} testId="nav-prospects" />
          <NavItem to="/mes-statistiques" icon={BarChart2} label="Mes statistiques" onClick={onClose} testId="nav-mes-statistiques" />
          <button
            className={`cbm-nav-link w-full ${location.pathname === "/poster-annonce" ? "active" : ""}`}
            onClick={() => { navigate("/poster-annonce"); onClose?.(); }}
            data-testid="nav-poster-annonce"
          >
            <Plus size={17} />
            <span>Poster une annonce</span>
          </button>
          <button
            className={`cbm-nav-link w-full ${location.pathname === "/avantages/new" ? "active" : ""}`}
            onClick={async () => {
              try {
                const { data } = await axios.get(`${API}/api/benefits/mine`, { withCredentials: true });
                if (data && (data.status === "pending" || data.status === "approved")) {
                  navigate("/mon-profil#mon-avantage");
                } else {
                  navigate("/avantages/new");
                }
              } catch {
                navigate("/avantages/new");
              }
              onClose?.();
            }}
            data-testid="nav-poster-avantage"
          >
            <Gift size={17} />
            <span>Poster un avantage</span>
          </button>
        </div>

        {user?.role === "admin" && (
          <div className="pt-4 border-t cbm-divider mt-4">
            <p className="cbm-label px-2 mb-3">Administration</p>
            <NavItem to="/admin" icon={Shield} label="Panneau Admin" onClick={onClose} testId="nav-admin" />
          </div>
        )}
      </nav>

      {/* Footer — Parrainage CTA */}
      <div className="px-4 py-3 border-t cbm-divider">
        <button
          data-testid="sponsor-button"
          onClick={() => { setShowSponsor(true); onClose?.(); }}
          className="w-full flex items-center justify-center gap-2 text-xs font-medium py-2 px-3 rounded-lg transition-all"
          style={{
            border: "1px solid rgba(201, 162, 39, 0.35)",
            color: "#C9A227",
            background: "transparent",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(201, 162, 39, 0.12)";
            e.currentTarget.style.borderColor = "rgba(201, 162, 39, 0.55)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "rgba(201, 162, 39, 0.35)";
          }}
        >
          <UserPlus size={13} /> Parrainer un membre
        </button>
      </div>

      {/* User + Logout */}
      <div className="px-3 py-3 border-t cbm-divider">
        <button
          data-testid="help-button"
          className="cbm-nav-link w-full text-cbm-secondary hover:text-gold hover:bg-gold/5"
          onClick={() => { setShowHelp(true); onClose?.(); }}
        >
          <HelpCircle size={17} />
          <span>Besoin d'aide</span>
        </button>
        <button
          data-testid="logout-button"
          className="cbm-nav-link w-full text-red-400 hover:text-red-300 hover:bg-red-900/10"
          onClick={handleLogout}
        >
          <LogOut size={17} />
          <span>Se déconnecter</span>
        </button>
        <div className="px-2 pt-2">
          <a
            href="/conditions"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cbm-muted hover:text-gold text-[10px] transition-colors block text-center"
            data-testid="sidebar-legal-link"
          >
            Conditions & confidentialité
          </a>
        </div>
        <div
          data-testid="app-version"
          className="text-cbm-muted text-[10px] text-center pt-1 select-none"
        >
          {APP_VERSION}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen" style={{ background: "#0D1B3E" }}>
      {/* Desktop sidebar */}
      <div className="hidden lg:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden animate-fade-in">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="relative z-10 h-full">
            <Sidebar onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* Mobile topbar */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b cbm-divider" style={{ background: "#071028" }}>
          <button className="text-cbm-secondary hover:text-white" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu size={20} />
          </button>
          <img src={LOGO_URL} alt="CBM" className="w-8 h-8 object-contain" />
          <div className="w-10" />
        </div>

        {/* Floating notification bell + updates button + user identity — fixed top-right on all screens */}
        <div className="fixed top-3 right-4 lg:top-4 lg:right-6 z-40 flex items-center gap-2">
          <UpdatesButton />
          <NotificationBell />
          <button
            onClick={() => navigate("/parametres")}
            className="flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all hover:bg-white/10"
            style={{ background: "#0D1B3E", border: "1px solid rgba(255,255,255,0.1)" }}
            data-testid="user-identity-button"
          >
            <div
              className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-semibold text-gold"
              style={{ background: "linear-gradient(135deg, #1a3a6e, #0D1B3E)", border: "1px solid rgba(201,162,39,0.3)" }}
            >
              {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
            </div>
            <span className="text-white text-sm font-medium hidden sm:block">{user?.name || user?.email}</span>
          </button>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto cbm-main-area">
          {children}
        </main>
      </div>

      {/* Sponsor modal */}
      <SponsorModal open={showSponsor} onClose={() => setShowSponsor(false)} />
      {/* Help modal */}
      <HelpModal open={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
}
